/**
 * PremiumPackageRemapper
 *
 * Maps between the data and domain layers for premium packages:
 *   - PremiumPackageStorage (SecureStorage) → Entity
 *   - PremiumPackageDto    (Firestore wire) → Entity (via fromJson)
 */
import type { PremiumPackageDto } from '@/data/dtos/premiumPackage/premiumPackageDto';
import type { JsonObject } from '@/data/types/json';
import type { PremiumPackageStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { PremiumPackageEntity } from '@/domain/entities/premiumPackage/premiumPackageEntity';

export class PremiumPackageRemapper {
  static toStorage(dto: PremiumPackageDto): PremiumPackageStorage {
    return {
      productId: dto.product_id,
      order: dto.order,
      name: dto.name,
      currency: dto.currency,
      propositions: dto.propositions,
      price: dto.price,
      actions: dto.actions,
      currencySymbol: dto.currencySymbol,
      earnedCoins: dto.earnedCoins,
    };
  }

  static fromStorage(storage: PremiumPackageStorage): PremiumPackageEntity {
    return {
      productId: storage.productId,
      order: storage.order,
      name: storage.name,
      currency: storage.currency,
      propositions: storage.propositions,
      price: storage.price,
      actions: storage.actions,
      currencySymbol: storage.currencySymbol,
      earnedCoins: storage.earnedCoins,
    };
  }

  static fromStorageList(storageList: PremiumPackageStorage[]): PremiumPackageEntity[] {
    return storageList.map(PremiumPackageRemapper.fromStorage);
  }

  /**
   * Defensive parse from raw Firestore `JsonObject` to a typed DTO.
   * Wire field names match the Flutter `@JsonKey` aliases —
   * lib/data/models/premium_package.dart:10-36. Notable alias:
   * `productId` is on the wire as `product_id`.
   */
  static fromJson(data: JsonObject): PremiumPackageDto {
    return {
      product_id: (data['product_id'] as string | undefined) ?? '',
      order: (data['order'] as number | undefined) ?? 0,
      name: (data['name'] as string | undefined) ?? '',
      currency: (data['currency'] as string | undefined) ?? '',
      propositions: (data['propositions'] as string[] | undefined) ?? [],
      price: (data['price'] as number | undefined) ?? 0,
      actions: (data['actions'] as number[] | undefined) ?? [],
      currencySymbol: (data['currencySymbol'] as string | undefined) ?? '',
      earnedCoins: data['earnedCoins'] as number | undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): PremiumPackageDto[] {
    return dataList.map(PremiumPackageRemapper.fromJson);
  }
}
